import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Sparkles, BookOpen, Heart, Star, Palette, Waves } from 'lucide-react';

const LandingPage = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#0F172A] via-[#1E293B] to-[#0F172A] text-white overflow-hidden relative">
      {/* Animated Background Stars */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {[...Array(50)].map((_, i) => (
          <div
            key={i}
            className="absolute bg-white rounded-full animate-pulse"
            style={{
              width: Math.random() * 3 + 1 + 'px',
              height: Math.random() * 3 + 1 + 'px',
              top: Math.random() * 100 + '%',
              left: Math.random() * 100 + '%',
              animationDelay: Math.random() * 2 + 's',
              animationDuration: Math.random() * 3 + 2 + 's',
            }}
          />
        ))}
      </div>

      {/* Hero Section */}
      <div className="relative z-10 container mx-auto px-6 py-16">
        <div className="text-center space-y-8 max-w-4xl mx-auto">
          <div className="inline-flex items-center gap-2 glass-panel px-4 py-2 rounded-full mb-6">
            <Sparkles className="w-4 h-4 text-amber-400" />
            <span className="text-sm text-amber-300">Now Open to Founding Families</span>
          </div>

          <h1 className="text-5xl sm:text-6xl lg:text-7xl font-bold font-philosopher glow-text leading-tight">
            Welcome to the
            <br />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-indigo-400 via-purple-400 to-pink-400">
              Temple of Children
            </span>
          </h1>

          <p className="text-lg sm:text-xl text-slate-300 max-w-2xl mx-auto leading-relaxed">
            An interactive digital learning world where children explore knowledge through
            magical portals. Each portal leads to unique lessons, activities, and adventures.
          </p>

          <div className="flex flex-col sm:flex-row gap-4 justify-center pt-6">
            <button
              data-testid="get-started-btn"
              onClick={() => navigate('/auth')}
              className="bg-gradient-to-r from-indigo-500 to-purple-500 hover:from-indigo-600 hover:to-purple-600 text-white rounded-full px-8 py-4 transition-all duration-300 hover:scale-105 shadow-lg shadow-indigo-500/25 text-lg font-semibold"
            >
              Get Started Free
            </button>
            <button
              data-testid="parent-login-btn"
              onClick={() => navigate('/auth')}
              className="bg-white/10 hover:bg-white/20 text-white border border-white/20 rounded-full px-8 py-4 transition-all duration-300 hover:scale-105 backdrop-blur-md shadow-[0_0_15px_rgba(255,255,255,0.1)] text-lg font-semibold"
            >
              Parent Login
            </button>
          </div>
          
          <p className="text-sm text-slate-500 mt-4">
            Limited to the first 100 families • Founding Family lifetime access available
          </p>
        </div>

        {/* Portal Realms Preview */}
        <div className="mt-24 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
          {[
            {
              name: 'Memory Garden',
              icon: BookOpen,
              color: 'text-green-400',
              bg: 'from-green-500/20 to-green-600/5',
              description: 'Stories, imagination, mythology, history',
            },
            {
              name: 'Chamber of Radiant Will',
              icon: Heart,
              color: 'text-amber-400',
              bg: 'from-amber-500/20 to-amber-600/5',
              description: 'Emotional intelligence, discipline, character',
            },
            {
              name: 'Star Map Hall',
              icon: Star,
              color: 'text-blue-400',
              bg: 'from-blue-500/20 to-blue-600/5',
              description: 'Science, astronomy, exploration',
            },
            {
              name: 'Creative Forge',
              icon: Palette,
              color: 'text-orange-400',
              bg: 'from-orange-500/20 to-orange-600/5',
              description: 'Art, music, creativity',
            },
            {
              name: 'Calm Cove',
              icon: Waves,
              color: 'text-teal-400',
              bg: 'from-teal-500/20 to-teal-600/5',
              description: 'Meditation, focus, mindfulness',
            },
          ].map((realm, i) => (
            <div
              key={i}
              className={`glass-panel p-6 rounded-2xl hover:border-white/20 transition-all duration-300 bg-gradient-to-br ${realm.bg}`}
            >
              <realm.icon className={`w-10 h-10 ${realm.color} mb-4 glow-portal`} />
              <h3 className="text-xl font-philosopher font-bold mb-2">{realm.name}</h3>
              <p className="text-slate-400 text-sm">{realm.description}</p>
            </div>
          ))}
        </div>

        {/* Features */}
        <div className="mt-24 text-center max-w-3xl mx-auto">
          <h2 className="text-3xl font-philosopher font-bold mb-12">How It Works</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="space-y-3">
              <div className="w-12 h-12 rounded-full bg-indigo-500/20 flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl font-bold text-indigo-400">1</span>
              </div>
              <h3 className="font-semibold text-lg">Create Profiles</h3>
              <p className="text-slate-400 text-sm">Parents create child profiles and manage their learning journey</p>
            </div>
            <div className="space-y-3">
              <div className="w-12 h-12 rounded-full bg-purple-500/20 flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl font-bold text-purple-400">2</span>
              </div>
              <h3 className="font-semibold text-lg">Explore Portals</h3>
              <p className="text-slate-400 text-sm">Children enter the temple and choose from five magical learning realms</p>
            </div>
            <div className="space-y-3">
              <div className="w-12 h-12 rounded-full bg-pink-500/20 flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl font-bold text-pink-400">3</span>
              </div>
              <h3 className="font-semibold text-lg">Track Progress</h3>
              <p className="text-slate-400 text-sm">Monitor completion, streaks, and growth across all realms</p>
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="relative z-10 text-center py-12 text-slate-500 text-sm">
        <p>&copy; 2025 Temple of Children. Nurturing young minds through wonder and wisdom.</p>
      </div>
    </div>
  );
};

export default LandingPage;
